import type { Input } from '../core/Input';

/** Buttons on the right: [label, key, hold (true) or tap]. */
const BUTTONS: [string, string, boolean][] = [
  ['Jump', 'Space', true],
  ['Down', 'KeyC', true],
  ['Throw', 'KeyF', false],
  ['Fly', 'KeyV', false],
  ['Lamp', 'KeyL', false],
  ['View', 'KeyO', false],
];
/** Joystick travel (px) that counts as a push, and as a sprint. */
const DEADZONE = 16;
const SPRINT = 58;
const RADIUS = 64;
/** Look speed: mouse pixels per touch pixel dragged. */
const LOOK = 2.4;

/**
 * Phone and tablet controls: a floating joystick on the left half of the screen
 * (push further to sprint), drag anywhere else to look, and round buttons for
 * jump, down, throw, fly, the lantern and the view (first person or over the
 * shoulder). The pause button returns to the start screen. Everything feeds the same
 * Input as keyboard, mouse and gamepad.
 */
export class TouchControls {
  private readonly root: HTMLElement;
  private readonly stick: HTMLElement;
  private readonly knob: HTMLElement;
  private stickId: number | null = null;
  private stickX = 0;
  private stickY = 0;
  private lookId: number | null = null;
  private lookX = 0;
  private lookY = 0;

  constructor(private readonly input: Input) {
    this.root = document.getElementById('touch')!;
    this.root.innerHTML = '';
    this.stick = div('touch-stick');
    this.knob = div('touch-knob');
    this.stick.appendChild(this.knob);
    this.root.appendChild(this.stick);
    const pad = div('touch-buttons');
    for (const [label, code, hold] of BUTTONS) {
      const b = div('touch-button');
      b.textContent = label;
      b.addEventListener('touchstart', (e) => {
        e.preventDefault();
        e.stopPropagation();
        input.setTouchKey(code, true);
        b.classList.add('down');
        if (!hold) setTimeout(() => input.setTouchKey(code, false), 120);
      });
      const up = (e: TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (hold) input.setTouchKey(code, false);
        b.classList.remove('down');
      };
      b.addEventListener('touchend', up);
      b.addEventListener('touchcancel', up);
      pad.appendChild(b);
    }
    this.root.appendChild(pad);
    const pause = div('touch-pause');
    pause.textContent = 'II';
    pause.addEventListener('touchstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
      input.setTouchActive(false);
    });
    this.root.appendChild(pause);

    this.root.addEventListener('touchstart', (e) => this.start(e), { passive: false });
    this.root.addEventListener('touchmove', (e) => this.move(e), { passive: false });
    this.root.addEventListener('touchend', (e) => this.end(e), { passive: false });
    this.root.addEventListener('touchcancel', (e) => this.end(e), { passive: false });
  }

  /** Shown only while playing with touch. */
  setVisible(on: boolean): void {
    this.root.classList.toggle('hidden', !on);
  }

  private start(e: TouchEvent): void {
    e.preventDefault();
    for (const t of e.changedTouches) {
      if (t.clientX < window.innerWidth * 0.45 && this.stickId === null) {
        this.stickId = t.identifier;
        this.stickX = t.clientX;
        this.stickY = t.clientY;
        this.stick.style.left = `${t.clientX}px`;
        this.stick.style.top = `${t.clientY}px`;
        this.stick.classList.add('active');
        this.knob.style.transform = 'translate(-50%, -50%)';
      } else if (this.lookId === null) {
        this.lookId = t.identifier;
        this.lookX = t.clientX;
        this.lookY = t.clientY;
      }
    }
  }

  private move(e: TouchEvent): void {
    e.preventDefault();
    for (const t of e.changedTouches) {
      if (t.identifier === this.stickId) {
        let dx = t.clientX - this.stickX;
        let dy = t.clientY - this.stickY;
        const len = Math.hypot(dx, dy);
        this.input.setTouchKey('KeyW', dy < -DEADZONE);
        this.input.setTouchKey('KeyS', dy > DEADZONE);
        this.input.setTouchKey('KeyA', dx < -DEADZONE);
        this.input.setTouchKey('KeyD', dx > DEADZONE);
        this.input.setTouchKey('ShiftLeft', len > SPRINT);
        if (len > RADIUS) {
          dx *= RADIUS / len;
          dy *= RADIUS / len;
        }
        this.knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
      } else if (t.identifier === this.lookId) {
        this.input.addLook((t.clientX - this.lookX) * LOOK, (t.clientY - this.lookY) * LOOK);
        this.lookX = t.clientX;
        this.lookY = t.clientY;
      }
    }
  }

  private end(e: TouchEvent): void {
    for (const t of e.changedTouches) {
      if (t.identifier === this.stickId) {
        this.stickId = null;
        this.stick.classList.remove('active');
        for (const k of ['KeyW', 'KeyS', 'KeyA', 'KeyD', 'ShiftLeft']) this.input.setTouchKey(k, false);
      } else if (t.identifier === this.lookId) {
        this.lookId = null;
      }
    }
  }
}

function div(className: string): HTMLDivElement {
  const d = document.createElement('div');
  d.className = className;
  return d;
}
